import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ColumnDef } from '../config/viewsConfig';
import type { EntityType } from '../types';
import { useLab } from '../LabContext';
import { readColumns, writeColumns } from '../utils/persistedColumns';

/**
 * Manage column visibility with lab-scoped localStorage persistence.
 * Handles key construction, read/write, and re-read on lab or schema change.
 *
 * `defaultColumns` is the default-visible set (used for fallback + clearing
 * storage when the selection matches it). `knownColumns` is the wider universe
 * used to resolve persisted keys back to ColumnDefs (e.g. optional curated
 * columns that are not visible by default); it defaults to `defaultColumns`.
 */
export function usePersistedColumns(
  baseKey: string,
  defaultColumns: ColumnDef[],
  entityType?: EntityType,
  knownColumns?: ColumnDef[],
): {
  columns: ColumnDef[];
  setColumns: (cols: ColumnDef[]) => void;
  storageKey: string;
} {
  const { labSelection } = useLab();
  const storageKey = `lab${labSelection}:${baseKey}`;
  const resolutionColumns = knownColumns ?? defaultColumns;

  const [columns, setColumnsRaw] = useState<ColumnDef[]>(
    () => readColumns(storageKey, resolutionColumns, entityType) ?? defaultColumns,
  );

  const columnsKey = useMemo(
    () => JSON.stringify(resolutionColumns.map(c => c.key)),
    [resolutionColumns],
  );

  useEffect(() => {
    setColumnsRaw(readColumns(storageKey, resolutionColumns, entityType) ?? defaultColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, columnsKey]);

  const setColumns = useCallback((cols: ColumnDef[]) => {
    setColumnsRaw(cols);
    writeColumns(storageKey, cols, defaultColumns);
  }, [storageKey, defaultColumns]);

  return { columns, setColumns, storageKey };
}
